<template lang="pug">
	.easy-creative--integration-settings--item.flex-col.space-medium
		Loader(v-if="isSaving || isLoading", text="", :fullscreen="true")
		DxTabPanel
			DxItem(:title="$tCaption('Configuration')")
				template(#default)
					.flex-col.space-large.padding
						.flex-col.space-medium
							.flex-row.space-large
								.flex-col
									.font-small.upper.grey {{$tCaption('Integration settings')}}
									.font-big.upper {{item.Name}}
								.flex-col
									.font-small.upper.grey {{$tCaption('API Key')}}
									.font-big.upper {{item.Oid}}
							.flex-col
								.font-small.upper.grey {{$tCaption('Callback URL')}}
								DxTextBox(:value="json.CallbackUrl", @value-changed="e => setJsonValue('CallbackUrl', e.value)")
							.flex-row
								DxCheckBox(:text="$tCaption('Require creative approve')", :value="json.RequireCreativeApprove || false", @value-changed="e => setJsonValue('RequireCreativeApprove', e.value)")
						template(v-if="isInitialized")
							.flex-col.space(v-if="briefQuestions && briefQuestions.length")
								.flex-row.align-center.space
									.upper.grey {{$tCaption('Predefined answers')}}
									DxButton(icon="plus", @click="addAnswer")
								.flex-col.space
									.answer.flex-row.space-medium.align-center(v-for="(answer, aIdx) in answers")
										.flex-col.flex-1.field
											.font-tiny.grey.upper {{$tCaption('Question')}}
											DxSelectBox(:data-source="briefQuestions", valueExpr="Oid", displayExpr="Question", :value.sync="answer.questionId")
										.flex-col.flex-1.field
											.font-tiny.grey.upper {{$tCaption('Answer')}}
											DxTextBox(:value.sync="answer.answer")
										.flex-row.space-medium
											DxCheckBox(:text="$tCaption('Disabled')", :value.sync="answer.disabled", :hint="$tCaption('Value changing in brief disabled')")
											DxCheckBox(:text="$tCaption('Hidden')", :value.sync="answer.hidden", :hint="$tCaption('Question is hidden in brief for end user')")
										DxButton(icon="fas fa-times", @click="answers.splice(aIdx, 1)", stylingMode="text")
							.flex-col.space
								.upper.grey {{$tCaption('Examples')}}
								DxTabPanel.tab-panel-small
									DxItem(:title="$tCaption('Static URL')")
										template(#default)
											.static-url-panel.flex-col.space-large
												.flex-row.space-medium
													.flex-col
														.font-small.upper.grey {{$tCaption('Brief mode')}}
														DxSelectBox(:value.sync="staticUrlData.briefMode", :items="['builder', 'templates']")
													.flex-col
														.font-small.upper.grey {{$tCaption('User name')}}
														DxTextBox(:value.sync="staticUrlData.userName")
													.flex-col
														.font-small.upper.grey {{$tCaption('Email')}}
														DxTextBox(:value.sync="staticUrlData.email")
													.flex-col.flex-1
														.font-small.upper.grey {{$tCaption('Custom Adthos URL')}}
														DxTextBox(:value.sync="staticUrlData.adthosUrl")
												.flex-col
													.font-big.upper.grey {{$tCaption('Static URL')}}
													a(:href="staticUrl").word-break-all {{getStaticUrl()}}
									DxItem(:title="$tCaption('Javascript widget')")
										template(#default)
											.static-url-panel.flex-col.space-large
												.flex-col.space-medium
													span {{$tCaption('1. Place link to Adthos AI Creative wrapper into the destination page header sources:')}}
													pre {{headerScriptSample}}
												.flex-col.space-medium
													span(v-html="$tCaption('2. When you are ready to show a widget need to call <b>adthos.init</b> function with supported parameters:')")
													.flex-row.space-large
														.flex-col.space-medium
															.flex-col.field
																.font-small.upper.grey {{$tCaption('Mode')}}
																DxSelectBox(:value.sync="staticUrlData.mode", :items="['widget', 'tab', 'popup', 'redirect']")
															.flex-col.field(v-if="staticUrlData.mode === 'widget'")
																.font-small.upper.grey {{$tCaption('Widget selector')}}
																DxTextBox(:value.sync="staticUrlData.widgetSelector")
															.flex-col.field
																.font-small.upper.grey {{$tCaption('Brief mode')}}
																DxSelectBox(:value.sync="staticUrlData.briefMode", :items="['builder', 'templates']")
															.flex-col.field
																.font-small.upper.grey {{$tCaption('User name')}}
																DxTextBox(:value.sync="staticUrlData.userName")
															.flex-col.field
																.font-small.upper.grey {{$tCaption('Email')}}
																DxTextBox(:value.sync="staticUrlData.email")
															.flex-col.field
																.font-small.upper.grey {{$tCaption('Custom Adthos URL')}}
																DxTextBox(:value.sync="staticUrlData.adthosUrl")
														.flex-col.space
															.font-small.upper.grey {{$tCaption('Script')}}
															pre {{callSample}}
</template>

<script>
	import _ from 'lodash';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxButton from 'devextreme-vue/button';
	import DxCheckBox from 'devextreme-vue/check-box';
	import DxSelectBox from 'devextreme-vue/select-box';
	import Loader from 'app-core/Loader.vue';
	import DxTabPanel, { DxItem } from 'devextreme-vue/tab-panel';

	export default {
		translationCategory: 'creative.EasyCreative',
		name: 'easy-creative--integration-settings--item',
		components: { DxTextBox, DxCheckBox, Loader, DxTabPanel, DxItem, DxSelectBox, DxButton },
		props: ['item'],
		data() {
			return {
				isSaving: 0,
				isLoading: 0,
				isInitialized: false,
				staticUrlData: {
					adthosUrl: null,
					mode: 'widget',
					email: 'test@test.com',
					userName: 'User name',
					briefMode: 'builder',
					widgetSelector: '#widget'
				},
				briefQuestions: []
			};
		},
		computed: {
			json() {
				return this.item.JsonConfig || {};
			},
			answers() {
				if (!this.json.answers) {
					this.$set(this.json, 'answers', []);
				}
				return this.json.answers;
			},
			formattedAnswers() {
				return this.json.answers
					.map(f => ({ answer: f, question: this.briefQuestions.find(q => q.Oid === f.questionId) }))
					.filter(f => f.question)
					.map(f => {
						let item = {};
						if (f.question.PromptValue) {
							item.prompt = f.question.PromptValue;
						} else {
							item.question = f.question.Question;
						}

						item.value = f.answer.answer;
						if (f.answer.disabled) {
							item.disabled = true;
						}

						if (f.answer.hidden) {
							item.hidden = true;
						}
						return item;
					});
			},
			staticUrl() {
				return this.getStaticUrl(true);
			},
			headerScriptSample() {
				return `<head>
	...
	<script src="http://portal.adthos.com/public/dist/integration-widget.min.js"><\/script>
<head>`;
			},
			callSample() {
				return `window.adthos.init({${this.staticUrlData.widgetSelector && this.staticUrlData.mode === 'widget' ? `
  widgetSelector: '${this.staticUrlData.widgetSelector}',` : ''}${this.staticUrlData.adthosUrl ? `
  adthosUrl: '${this.staticUrlData.adthosUrl}',` : ''}
  mode: '${this.staticUrlData.mode}',
  apiKey: '${this.item.Oid}',
  userName: '${this.staticUrlData.userName}',
  email: '${this.staticUrlData.email}',
  answers: ${this.formattedAnswers?.length ? JSON.stringify(this.formattedAnswers, void 0, '  ').replace(/\n/g, '\n  ') : null}
});`
			}
		},
		watch: {
			answers: {
				handler() {
					this.$save();
				},
				deep: true
			}
		},
		mounted() {
			if (__DEBUG__)
				window.easyCreativeIngegrationSettingsItem = this;
			this.$save = _.debounce(this.save, 500, {
				leading: false,
				trailing: true
			});

			this.loadBriefQuestions();
		},
		destroyed() {
			delete this.$save;
		},
		methods: {
			getStaticUrl(encoded) {
				const adthosUrl = this.staticUrlData?.adthosUrl || (window.location?.hostname === 'localhost' ? 'http://localhost:8080' : 'https://portal.adthos.com/public/dist/main.html');

				const params = {
					mode: this.staticUrlData?.briefMode || 'builder',
					email: this.staticUrlData?.email,
					userName: this.staticUrlData?.userName,
					apiKey: this.item?.Oid,
					answers: this.formattedAnswers?.length ? JSON.stringify(this.formattedAnswers) : null
				};

				const paramsString = Object.keys(params).filter(f => params[f]).map(f => `${f}=${encoded ? encodeURIComponent(params[f]) : params[f]}`).join('&');
				const url = adthosUrl + `/#/easy-creative-integration?${paramsString}`;

				return url;
			},
			addAnswer() {
				this.answers.push({
					questionId: null,
					answer: null,
					disabled: false,
					hidden: false
				});
			},
			async loadBriefQuestions() {
				this.isLoading++;
				try {
					const steps = await this.$Data.getExternalApi('BriefStep/GetAllSteps', `briefId=${this.item.Brief?.Oid}`, 999).then(r => r?.Results || []);
					this.briefQuestions = (await Promise.all(steps.map(f => this.$Data.getExternalApi('BriefItem/GetAllBriefItems', `stepId=${f.Oid}`, 999).then(r => r?.Results || [])))).reduce((res, arr) => [...res, ...arr], []);
				} finally {
					this.isLoading--;
					this.isInitialized = true;
				}
			},
			setJsonValue(prop, val) {
				if (!this.item.JsonConfig) {
					this.$set(this.item, 'JsonConfig', {});
				}
				this.$set(this.item.JsonConfig, prop, val);
				this.$save();
			},
			save() {
				this.isSaving++;
				if (this.isSaving > 1) return;
				this.$Data.postExternalApi('BriefIntegrationSetting/CreateUpdate', this.item).finally(() => {
					if (this.isSaving > 1) {
						this.isSaving = 0;
						this.save();
					} else {
						this.isSaving = 0;
					}
				})
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";
	.easy-creative--integration-settings--item {
		.static-url-panel {
			background: #fff;
			padding: 20px;
		}

		pre {
			margin: 0px !important;
		}
	}
</style>
